/**
 * Constellation SVG export (doc 147 §9 Phase 6 — "Export sub-graphs as
 * SVG/PNG"; §8.9.6 revised for theming).
 *
 * `buildConstellationSvg` turns a graphology graph (with the x/y/color/size
 * attributes `buildGraph` assigns) into a standalone, self-contained SVG
 * string — pure and fully testable. PNG export layers a canvas raster on top
 * of this in the view (browser-only, coverage-excluded there).
 *
 * Export is mode-aware: passing `{ mode: 'light' }` swaps the background and
 * edge stroke to the light-palette values. The caller (`ConstellationView`)
 * passes the user's current resolved mode so the exported SVG matches what
 * they see on screen.
 */
import type Graph from 'graphology';
import { dsPalettes, graphTokens, type Mode } from '../theme/tokens.js';

export interface SvgExportOptions {
  /** Output viewport size; the graph is scaled to fit with padding. */
  width?: number;
  height?: number;
  padding?: number;
  /** Explicit background hex; defaults to the active mode's `--graph-bg`. */
  background?: string;
  /** Palette mode for the default background + edge stroke. */
  mode?: Mode;
}

interface NodePoint {
  id: string;
  x: number;
  y: number;
  size: number;
  color: string;
  label: string;
}

function escapeXml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/**
 * Render a graphology graph as a standalone SVG document string. Node
 * coordinates are normalized into the viewport; edges are drawn first so
 * nodes sit on top.
 */
export function buildConstellationSvg(graph: Graph, options: SvgExportOptions = {}): string {
  const width = options.width ?? 1200;
  const height = options.height ?? 800;
  const padding = options.padding ?? 40;
  const mode: Mode = options.mode ?? 'dark';
  const palette = dsPalettes[mode];
  // SVG export uses the standard-contrast graph tokens — the high-contrast
  // toggle is for live screen viewing; the export is a stable artefact.
  const graphPalette = graphTokens[mode].standard;
  const background = options.background ?? graphPalette['graph-bg']!;
  // Solid-colour stroke for SVG (no compositing); use a darker derived value
  // so static documents (PR comments, audit archives) read well in B&W print.
  const edgeStroke = mode === 'light' ? palette.text3! : palette.text2!;
  const nodeFallback = palette.text2!;

  const nodes: NodePoint[] = [];
  graph.forEachNode((id, attr) => {
    nodes.push({
      id,
      x: Number(attr.x) || 0,
      y: Number(attr.y) || 0,
      size: Number(attr.size) || 4,
      color: typeof attr.color === 'string' ? attr.color : nodeFallback,
      label: typeof attr.label === 'string' ? attr.label : id,
    });
  });

  if (nodes.length === 0) {
    return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}"><rect width="${width}" height="${height}" fill="${background}"/></svg>`;
  }

  // Normalize the raw layout coordinates into the padded viewport.
  const xs = nodes.map((n) => n.x);
  const ys = nodes.map((n) => n.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);
  const spanX = maxX - minX || 1;
  const spanY = maxY - minY || 1;
  const project = (n: NodePoint): { px: number; py: number } => ({
    px: padding + ((n.x - minX) / spanX) * (width - 2 * padding),
    py: padding + ((n.y - minY) / spanY) * (height - 2 * padding),
  });
  const positioned = new Map(nodes.map((n) => [n.id, { node: n, ...project(n) }]));

  const edgeLines: string[] = [];
  graph.forEachEdge((_edge, _attr, source, target) => {
    const a = positioned.get(source);
    const b = positioned.get(target);
    if (!a || !b) return;
    edgeLines.push(
      `<line x1="${a.px.toFixed(1)}" y1="${a.py.toFixed(1)}" x2="${b.px.toFixed(1)}" y2="${b.py.toFixed(1)}" stroke="${edgeStroke}" stroke-width="0.5" stroke-opacity="0.6"/>`,
    );
  });

  const nodeCircles = [...positioned.values()].map(
    ({ node, px, py }) =>
      `<circle cx="${px.toFixed(1)}" cy="${py.toFixed(1)}" r="${node.size}" fill="${node.color}"><title>${escapeXml(node.label)}</title></circle>`,
  );

  return [
    `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 ${width} ${height}">`,
    `<rect width="${width}" height="${height}" fill="${background}"/>`,
    `<g class="edges">${edgeLines.join('')}</g>`,
    `<g class="nodes">${nodeCircles.join('')}</g>`,
    `</svg>`,
  ].join('');
}
